import { Container, Graphics } from 'pixi.js';
import type {
  Snapshot,
  EngineEvent,
  EnemySnapshot,
  BulletSnapshot,
  PickupSnapshot,
  PlayerSnapshot,
  BossSnapshot,
} from '@nebulark/engine';
import { palette, WORLD_W, WORLD_H } from './theme';
import { SpritePool } from './spritePool';
import {
  makePlayerSprite,
  makeEnemySprite,
  makeBulletSpreadSprite,
  makeBulletLaserSprite,
  makeBulletHomingSprite,
  makeBulletEnemySprite,
  makeBulletBossSprite,
  makePickupSprite,
  makeShieldSprite,
  makeBossSprite,
} from './factories';
import { ParticleLayer } from './particles';
import { Camera } from './camera';
import { Hud } from './hud';

/**
 * Renderer reconciles a per-tick engine Snapshot into Pixi containers.
 * Uses pooled Graphics sprites keyed by entity id. Pure read-side: never
 * mutates engine state.
 */
export class Renderer {
  readonly root: Container;
  readonly camera: Camera;
  readonly gameContainer: Container;
  readonly hud: Hud;

  private readonly bgContainer: Container;
  private readonly bgGrid: Graphics;
  private readonly bgNebula: Graphics;
  private readonly bgStars1: Graphics;
  private readonly bgStars2: Graphics;
  private readonly entitiesContainer: Container;
  private readonly particleLayer: ParticleLayer;

  private readonly playerPool = new SpritePool<Graphics>(makePlayerSprite);
  private readonly enemyPools = new Map<string, SpritePool<Graphics>>();
  private readonly bulletPools: Record<string, SpritePool<Graphics>>;
  private readonly pickupPool = new SpritePool<Graphics>(makePickupSprite);
  private readonly bossPool = new SpritePool<Container>(makeBossSprite);

  private readonly livePlayers = new Map<number, Graphics>();
  private readonly shieldPool = new SpritePool<Graphics>(makeShieldSprite);
  private readonly liveShields = new Map<number, Graphics>();
  private readonly liveEnemies = new Map<number, { g: Graphics; archetype: string }>();
  private readonly liveBullets = new Map<number, { g: Graphics; kind: string }>();
  private readonly livePickups = new Map<number, Graphics>();
  private liveBoss: { c: Container; id: number } | null = null;

  private eventListener: ((e: EngineEvent) => void) | null = null;

  constructor() {
    this.root = new Container();
    this.root.sortableChildren = false;

    this.camera = new Camera();
    this.gameContainer = this.camera.container;
    this.root.addChild(this.gameContainer);

    this.bgContainer = new Container();
    this.gameContainer.addChild(this.bgContainer);

    this.bgNebula = this.buildNebula();
    this.bgContainer.addChild(this.bgNebula);

    this.bgStars1 = this.buildStarLayer(70, 1, 1.5);
    this.bgContainer.addChild(this.bgStars1);

    this.bgStars2 = this.buildStarLayer(40, 1.5, 2.5);
    this.bgContainer.addChild(this.bgStars2);

    this.bgGrid = new Graphics();
    this.drawBgGrid();
    this.bgContainer.addChild(this.bgGrid);

    this.entitiesContainer = new Container();
    this.gameContainer.addChild(this.entitiesContainer);

    this.particleLayer = new ParticleLayer();
    this.gameContainer.addChild(this.particleLayer.container);

    this.hud = new Hud();
    this.root.addChild(this.hud.container);

    this.bulletPools = {
      spread: new SpritePool<Graphics>(makeBulletSpreadSprite),
      laser: new SpritePool<Graphics>(makeBulletLaserSprite),
      homing: new SpritePool<Graphics>(makeBulletHomingSprite),
      'enemy-basic': new SpritePool<Graphics>(makeBulletEnemySprite),
      'enemy-aimed': new SpritePool<Graphics>(makeBulletEnemySprite),
      boss: new SpritePool<Graphics>(makeBulletBossSprite),
    };
  }

  onEvent(cb: (e: EngineEvent) => void): void {
    this.eventListener = cb;
  }

  emit(events: readonly EngineEvent[]): void {
    if (!this.eventListener) return;
    for (const e of events) this.eventListener(e);
  }

  /** Main per-frame call. dtMs is renderer-frame dt (for shake decay). */
  render(snapshot: Snapshot, dtMs: number): void {
    // Background scroll (illusion of movement). The grid just y-offsets.
    const scrollSpeed = 60; // logical px/sec
    const offset = (snapshot.timeMs * scrollSpeed) / 1000;
    this.bgGrid.y = offset % 40;

    // Parallax star layers scroll at different speeds.
    this.bgStars1.y = (snapshot.timeMs * 18) / 1000 % WORLD_H;
    this.bgStars2.y = (snapshot.timeMs * 35) / 1000 % WORLD_H;

    this.reconcilePlayers(snapshot.players);
    this.reconcileEnemies(snapshot.enemies);
    this.reconcileBullets(snapshot.bullets);
    this.reconcilePickups(snapshot.pickups);
    this.reconcileBoss(snapshot.boss);
    this.particleLayer.update(snapshot.particles);

    this.camera.setIntent(snapshot.camera.shake);
    this.camera.update(dtMs);

    this.hud.update(snapshot);
  }

  private drawBgGrid(): void {
    const g = this.bgGrid;
    g.clear();
    // A couple of grids drawn stacked so we can tile by scrolling.
    const step = 40;
    const color = 0x0e1530;
    const alpha = 0.8;
    for (let y = -step; y < WORLD_H + step * 2; y += step) {
      g.moveTo(0, y).lineTo(WORLD_W, y).stroke({ color, width: 1, alpha });
    }
    for (let x = 0; x <= WORLD_W; x += step) {
      g.moveTo(x, -step).lineTo(x, WORLD_H + step).stroke({ color, width: 1, alpha });
    }
  }

  private reconcilePlayers(players: readonly PlayerSnapshot[]): void {
    const seen = new Set<number>();
    for (const p of players) {
      seen.add(p.id);
      let g = this.livePlayers.get(p.id);
      if (!g) {
        g = this.playerPool.acquire();
        this.entitiesContainer.addChild(g);
        this.livePlayers.set(p.id, g);
      }
      g.visible = p.alive;
      g.x = p.pos.x;
      g.y = p.pos.y;
      // Invuln flash
      g.alpha = p.invulnMs > 0 ? (Math.floor(p.invulnMs / 60) % 2 === 0 ? 0.4 : 1) : 1;

      // Shield ring
      if (p.alive && p.shieldHp > 0) {
        let sg = this.liveShields.get(p.id);
        if (!sg) {
          sg = this.shieldPool.acquire();
          this.entitiesContainer.addChild(sg);
          this.liveShields.set(p.id, sg);
        }
        sg.x = p.pos.x;
        sg.y = p.pos.y;
        sg.visible = true;
        sg.alpha = 0.7 + Math.sin(Date.now() / 150) * 0.3;
      } else {
        const sg = this.liveShields.get(p.id);
        if (sg) {
          this.entitiesContainer.removeChild(sg);
          this.shieldPool.release(sg);
          this.liveShields.delete(p.id);
        }
      }
    }
    for (const [id, g] of this.livePlayers) {
      if (!seen.has(id)) {
        this.entitiesContainer.removeChild(g);
        this.playerPool.release(g);
        this.livePlayers.delete(id);
        // Also clean up shield if present
        const sg = this.liveShields.get(id);
        if (sg) {
          this.entitiesContainer.removeChild(sg);
          this.shieldPool.release(sg);
          this.liveShields.delete(id);
        }
      }
    }
  }

  private reconcileEnemies(enemies: readonly EnemySnapshot[]): void {
    const seen = new Set<number>();
    for (const e of enemies) {
      seen.add(e.id);
      const existing = this.liveEnemies.get(e.id);
      let g: Graphics;
      if (!existing || existing.archetype !== e.archetype) {
        if (existing) {
          this.entitiesContainer.removeChild(existing.g);
          this.getEnemyPool(existing.archetype).release(existing.g);
        }
        g = this.getEnemyPool(e.archetype).acquire();
        this.entitiesContainer.addChild(g);
        this.liveEnemies.set(e.id, { g, archetype: e.archetype });
      } else {
        g = existing.g;
      }
      g.x = e.pos.x;
      g.y = e.pos.y;
      g.alpha = e.flashMs > 0 ? 0.5 : 1;
    }
    for (const [id, ent] of this.liveEnemies) {
      if (!seen.has(id)) {
        this.entitiesContainer.removeChild(ent.g);
        this.getEnemyPool(ent.archetype).release(ent.g);
        this.liveEnemies.delete(id);
      }
    }
  }

  private getEnemyPool(archetype: string): SpritePool<Graphics> {
    let pool = this.enemyPools.get(archetype);
    if (!pool) {
      pool = new SpritePool<Graphics>(() => makeEnemySprite(archetype));
      this.enemyPools.set(archetype, pool);
    }
    return pool;
  }

  private reconcileBullets(bullets: readonly BulletSnapshot[]): void {
    const seen = new Set<number>();
    for (const b of bullets) {
      seen.add(b.id);
      const existing = this.liveBullets.get(b.id);
      let g: Graphics;
      if (!existing || existing.kind !== b.kind) {
        if (existing) {
          this.entitiesContainer.removeChild(existing.g);
          this.getBulletPool(existing.kind).release(existing.g);
        }
        g = this.getBulletPool(b.kind).acquire();
        this.entitiesContainer.addChild(g);
        this.liveBullets.set(b.id, { g, kind: b.kind });
      } else {
        g = existing.g;
      }
      g.x = b.pos.x;
      g.y = b.pos.y;
    }
    for (const [id, ent] of this.liveBullets) {
      if (!seen.has(id)) {
        this.entitiesContainer.removeChild(ent.g);
        this.getBulletPool(ent.kind).release(ent.g);
        this.liveBullets.delete(id);
      }
    }
  }

  private getBulletPool(kind: string): SpritePool<Graphics> {
    const pool = this.bulletPools[kind];
    if (pool) return pool;
    // Fallback: use spread visuals for unknown kinds.
    return this.bulletPools.spread!;
  }

  private reconcilePickups(pickups: readonly PickupSnapshot[]): void {
    const seen = new Set<number>();
    for (const p of pickups) {
      seen.add(p.id);
      let g = this.livePickups.get(p.id);
      if (!g) {
        g = this.pickupPool.acquire();
        this.entitiesContainer.addChild(g);
        this.livePickups.set(p.id, g);
      }
      g.x = p.pos.x;
      g.y = p.pos.y;
      g.tint = pickupTintFor(p.weapon);
    }
    for (const [id, g] of this.livePickups) {
      if (!seen.has(id)) {
        this.entitiesContainer.removeChild(g);
        this.pickupPool.release(g);
        this.livePickups.delete(id);
      }
    }
  }

  private buildNebula(): Graphics {
    const g = new Graphics();
    // Deep nebula blobs — colored soft circles at very low alpha.
    g.circle(60, 180, 140).fill({ color: 0x3311aa, alpha: 0.07 });
    g.circle(310, 320, 110).fill({ color: 0x220055, alpha: 0.09 });
    g.circle(180, 500, 160).fill({ color: 0x004466, alpha: 0.06 });
    g.circle(80, 420, 90).fill({ color: 0x551100, alpha: 0.08 });
    g.circle(290, 90, 120).fill({ color: 0x113300, alpha: 0.07 });
    g.circle(170, 260, 80).fill({ color: 0x330033, alpha: 0.10 });
    return g;
  }

  private buildStarLayer(count: number, minR: number, maxR: number): Graphics {
    const g = new Graphics();
    // Generate stars spread over 2× world height so wrapping is seamless.
    // Use a simple LCG for deterministic layout (no Math.random dependency).
    let seed = 0x9e3779b9;
    const rand = (): number => {
      seed = (seed ^ (seed << 13)) >>> 0;
      seed = (seed ^ (seed >> 7)) >>> 0;
      seed = (seed ^ (seed << 17)) >>> 0;
      return seed / 0xffffffff;
    };
    for (let i = 0; i < count; i++) {
      const x = rand() * WORLD_W;
      const y = rand() * WORLD_H * 2;
      const r = minR + rand() * (maxR - minR);
      const alpha = 0.3 + rand() * 0.7;
      g.circle(x, y, r).fill({ color: 0xffffff, alpha });
    }
    return g;
  }

  private reconcileBoss(boss: BossSnapshot | null): void {
    if (!boss) {
      if (this.liveBoss) {
        this.entitiesContainer.removeChild(this.liveBoss.c);
        this.bossPool.release(this.liveBoss.c);
        this.liveBoss = null;
      }
      return;
    }
    if (!this.liveBoss || this.liveBoss.id !== boss.id) {
      if (this.liveBoss) {
        this.entitiesContainer.removeChild(this.liveBoss.c);
        this.bossPool.release(this.liveBoss.c);
      }
      const c = this.bossPool.acquire();
      this.entitiesContainer.addChild(c);
      this.liveBoss = { c, id: boss.id };
    }
    const c = this.liveBoss.c;
    c.x = boss.pos.x;
    c.y = boss.pos.y;
    c.alpha = boss.flashMs > 0 ? 0.5 : 1;
  }
}

function pickupTintFor(weapon: string): number {
  switch (weapon) {
    case 'spread':
      return palette.pickupSpread;
    case 'laser':
      return palette.pickupLaser;
    case 'homing':
      return palette.pickupHoming;
    case 'shield':
      return palette.pickupShield;
    default:
      return palette.pickup;
  }
}
